import { ReactElement, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import Loader from '@app/common/loadData/Loader';
import DeleteOrder from '../order/deleteOrderModal';
import PreviousStageOrder from '../order/previousStageOrderModal';
import useOrderDetails from '@app/healthcheck/order/useOrderDetails';
import useWindowWidth from '@app/common/useWindowWidth';
import { OrderDetailsI } from '@app/healthcheck/order/types';
import PageDesktop from './widthWindow/Desktop';
import PageMobile from './widthWindow/Mobile';
import { GlobalStyled } from './styles';
import useStartTimer from './person/useStartTimer';
import useConfirmOrder from './person/useOrderConfirm';
import useGenerateCertificate from './person/useGenerateCertificate';
import useResendCertificate from './person/useResendCertificate';
import Results from './results';
import RefreshOTPModal from '../refreshOTP/refreshOTPModal';
import EditOrderFormOrder from '../order/editOrderFormOrder';
import EditOrderFormPersonal from '../order/editOrderFormPersonal';
import showNotification from '@designSystem/toastNotification/selectToast';
import Lang from '@lang/en';

export interface PageProps {
  data: OrderDetailsI;
  handleCloseModal: () => void;
  handleOpenModalPersonal: () => void;
  handleOpenModalOrder: () => void;
  handleAddResultsOpen: () => void;
  handleTestPerformed: () => void;
  handleOrderConfirmed: () => void;
  handleDeleteOrderOpen: () => void;
  handlePreviousStageOrderOpen: () => void;
  handleResendCertificate: () => void;
  handleGenerateCertificate: () => void;
  showPreviousStageAndGenerateCertificateButtons: boolean;
  isOrderStatusDone?: boolean;
}

const OrderPage = (): ReactElement => {
  const showPreviousStageAndGenerateCertificateButtons = false;
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const params = useParams<{ id: string }>();
  const orderId = +params.id;
  const {
    orderDetails,
    loading,
    setShouldReload,
    otpExpireTime,
    error,
  } = useOrderDetails(orderId);
  const isOrderStatusDone = !error && orderDetails.status === 'done';
  const { startTimer } = useStartTimer();
  const { confirmOrder } = useConfirmOrder();
  const { resendCertificate } = useResendCertificate();
  const { generateCertificate } = useGenerateCertificate();

  useEffect(() => {
    if (error) {
      showNotification({
        text: Lang.healthCheckOrderDetailsError,
        type: 'error',
      });
    }
  }, [error, orderDetails]);

  const handleTestPerformed = async () => {
    await startTimer(orderId);
    setShouldReload(true);
  };

  const handleOrderConfirmed = async () => {
    await confirmOrder(orderId);
    setShouldReload(true);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  const handleOpenModalPersonal = () => {
    setOpenModal('personalModal');
  };

  const handleOpenModalOrder = () => {
    setOpenModal('orderModal');
  };

  const handleRefreshOTPModalOpen = () => {
    setOpenModal('OTPModal');
  };

  const shouldShowOTPModal = (otpExpireTime: number | null) => {
    if (!otpExpireTime) return false;

    const currentTimestamp = Date.now();
    return otpExpireTime <= currentTimestamp;
  };

  const handleAddResultsOpen = () => {
    if (shouldShowOTPModal(otpExpireTime)) {
      handleRefreshOTPModalOpen();
      return;
    }
    setOpenModal('addResultsModal');
  };

  const handleDeleteOrderOpen = () => {
    setOpenModal('deleteModal');
  };

  const handlePreviousStageOrderOpen = () => {
    setOpenModal('previousStageModal');
  };

  const handleResendCertificate = () => {
    resendCertificate(orderId);
  };

  const handleGenerateCertificate = () => {
    generateCertificate(orderId);
  };

  const { isMobileView } = useWindowWidth();

  const getResultContentClassName = () => {
    let className = isImageLoaded
      ? 'resultsModalContainerWithImage'
      : 'resultsModalContainer';
    className += isMobileView ? ' mobileResultContent' : '';

    return className;
  };

  const handlers = {
    handleCloseModal,
    handleOpenModalPersonal,
    handleOpenModalOrder,
    handleAddResultsOpen,
    handleOrderConfirmed,
    handleTestPerformed,
    handleDeleteOrderOpen,
    handlePreviousStageOrderOpen,
    handleGenerateCertificate,
    handleResendCertificate,
  };

  if (error) {
    return <Redirect to="/dashboard" />;
  }

  return loading ? (
    <Loader />
  ) : (
    <>
      <GlobalStyled />
      <Modal
        data-testid="modal-window-order-page-edit-order"
        dialogClassName={isMobileView ? 'mobileOrderContainer' : ''}
        contentClassName={isMobileView ? 'mobileOrderContent' : ''}
        fullscreen={isMobileView}
        onHide={handleCloseModal}
        show={openModal === 'orderModal'}
      >
        <EditOrderFormOrder
          handleClose={handleCloseModal}
          id={orderId}
          setShouldReload={setShouldReload}
          data-testid="form-modal-window-order-page-edit-order"
        />
      </Modal>
      <Modal
        data-testid="modal-window-order-page-edit-personal"
        dialogClassName={isMobileView ? 'mobileOrderContainer' : ''}
        contentClassName={isMobileView ? 'mobileOrderContent' : ''}
        fullscreen={isMobileView}
        onHide={handleCloseModal}
        show={openModal === 'personalModal'}
      >
        <EditOrderFormPersonal
          handleClose={handleCloseModal}
          id={orderId}
          setShouldReload={setShouldReload}
          data-testid="form-modal-window-order-page-edit-personal"
          isOrderStatusDone={isOrderStatusDone}
        />
      </Modal>
      <Modal
        data-testid="healthCheck-add-results-modal"
        dialogClassName={isMobileView ? 'mobileResultContainer' : ''}
        contentClassName={getResultContentClassName()}
        fullscreen={isMobileView}
        onHide={handleCloseModal}
        show={openModal === 'addResultsModal'}
      >
        <Results
          handleClose={handleCloseModal}
          orderId={orderId}
          setImageLoaded={setIsImageLoaded}
          setShouldReload={setShouldReload}
          isImageRequired={orderDetails.testType === 'antigen'}
          fullName={`${orderDetails.firstName} ${orderDetails.lastName}`}
          methodText={orderDetails.method}
        />
      </Modal>
      <Modal
        data-testid="healthCheck-delete-order-modal"
        dialogClassName={isMobileView ? 'mobileDeleteOrderContainer' : ''}
        contentClassName={isMobileView ? 'mobileDeleteOrderContent' : ''}
        fullscreen={isMobileView}
        onHide={handleCloseModal}
        show={openModal === 'deleteModal'}
      >
        <DeleteOrder
          handleClose={handleCloseModal}
          orderId={orderId}
          data-testid="modal-window-delete-order"
        />
      </Modal>
      <Modal
        data-testid="healthCheck-previous-stage-order-modal"
        dialogClassName={
          isMobileView ? 'mobilePreviousStageOrderContainer' : ''
        }
        contentClassName={isMobileView ? 'mobilePreviousStageOrderContent' : ''}
        fullscreen={isMobileView}
        onHide={handleCloseModal}
        show={openModal === 'previousStageModal'}
      >
        <PreviousStageOrder
          orderId={orderId}
          handleClose={handleCloseModal}
          data-testid="modal-window-previous-stage-order"
        />
      </Modal>
      <Modal show={openModal === 'OTPModal'}>
        <RefreshOTPModal
          setShouldReload={setShouldReload}
          handleClose={handleCloseModal}
        />
      </Modal>

      {isMobileView ? (
        <PageMobile
          data-testid="order-page-mobile"
          data={orderDetails}
          showPreviousStageAndGenerateCertificateButtons={
            showPreviousStageAndGenerateCertificateButtons
          }
          isOrderStatusDone={isOrderStatusDone}
          {...handlers}
        />
      ) : (
        <PageDesktop
          data-testid="order-page-desktop"
          data={orderDetails}
          showPreviousStageAndGenerateCertificateButtons={
            showPreviousStageAndGenerateCertificateButtons
          }
          isOrderStatusDone={isOrderStatusDone}
          {...handlers}
        />
      )}
    </>
  );
};

export default OrderPage;
